import { Injectable, inject } from '@angular/core';
import { HttpXhrBackend } from '@angular/common/http';
import { XhrFactory } from '@angular/common';
import {
  HttpClientBackendService,
  InMemoryBackendConfig,
  InMemoryDbService,
} from 'angular-in-memory-web-api';

/**
 * Drop-in replacement for `HttpClientBackendService` that resolves the
 * pass-thru backend through Angular DI instead of instantiating it manually.
 *
 * Background:
 * - When `passThruUnknownUrl: true`, the upstream service instantiates
 *   `new HttpXhrBackend(xhrFactory)` during request handling.
 * - In Angular 21, `HttpXhrBackend` uses field initializers that call
 *   `inject()`, which requires an active injection context.
 * - Request handling runs outside an injection context, so the upstream
 *   behavior triggers `NG0203` and surfaces as
 *   "Cannot create passThru404 backend; ...".
 *
 * Fix: `HttpXhrBackend` is `providedIn: 'root'`, so `inject(HttpXhrBackend)`
 * during this service's construction (which is itself an injection context)
 * gives us a fully initialized instance we can reuse for every passthrough
 * request.
 */
@Injectable()
export class InMemoryPassthruBackendService extends HttpClientBackendService {
  private readonly xhrBackend = inject(HttpXhrBackend);

  constructor() {
    super(
      inject(InMemoryDbService),
      inject(InMemoryBackendConfig),
      inject(XhrFactory),
    );
  }

  /**
   * Return the DI-provided `HttpXhrBackend` so it is constructed inside an
   * injection context rather than via `new HttpXhrBackend(...)` at request
   * time.
   * @returns The shared `HttpXhrBackend` instance used for passthrough requests.
   */
  protected override createPassThruBackend(): HttpXhrBackend {
    return this.xhrBackend;
  }
}
